import { defineStateMachine, type TransitionTable } from "../../../shared/state/stateMachine";

/**
 * The browser's one live socket (`GET /threads/live`, threads.md T-15), shared by every
 * Thread the app shows. It is wanted while at least one Thread is watched. A dropped socket
 * is opened again after a growing delay, and every subscription resumes after the last
 * entry the browser holds, so nothing is missed or repeated.
 *
 * The server closes with 4401 when the session ended (the app then asks to sign in again)
 * and with 4403 when this page's origin is not allowed; neither is retried by itself.
 */
export type LiveConnectionState =
  | { readonly status: "idle" }
  | { readonly status: "connecting"; readonly failures: number }
  | { readonly status: "open" }
  | { readonly status: "waitingToRetry"; readonly failures: number; readonly retryInMilliseconds: number }
  | { readonly status: "sessionEnded" }
  | { readonly status: "refused" };

export type LiveConnectionEvent =
  /** Something is watched now. */
  | { readonly type: "wanted" }
  | { readonly type: "opened" }
  | { readonly type: "closed"; readonly code: number }
  | { readonly type: "retryDue" }
  /** Nothing is watched any more, or the app signed out. */
  | { readonly type: "unwanted" }
  /** The user asked to try again, signed in again, or the browser came back online. */
  | { readonly type: "retryRequested" };

export const LIVE_CLOSE_SESSION_ENDED = 4401;
export const LIVE_CLOSE_ORIGIN_REFUSED = 4403;

const FIRST_RETRY_MILLISECONDS = 500;
const LONGEST_RETRY_MILLISECONDS = 30_000;

/** 0.5 s after a drop, then doubling per failed attempt, up to 30 s. */
export function retryDelayMilliseconds(failures: number): number {
  return Math.min(LONGEST_RETRY_MILLISECONDS, FIRST_RETRY_MILLISECONDS * 2 ** failures);
}

export const INITIAL_LIVE_CONNECTION_STATE: LiveConnectionState = { status: "idle" };

const idle = (): LiveConnectionState => ({ status: "idle" });
const connectingAfresh = (): LiveConnectionState => ({ status: "connecting", failures: 0 });

function afterClose(code: number, failures: number): LiveConnectionState {
  if (code === LIVE_CLOSE_SESSION_ENDED) {
    return { status: "sessionEnded" };
  }
  if (code === LIVE_CLOSE_ORIGIN_REFUSED) {
    return { status: "refused" };
  }
  return { status: "waitingToRetry", failures, retryInMilliseconds: retryDelayMilliseconds(failures) };
}

const LIVE_CONNECTION_TRANSITIONS: TransitionTable<LiveConnectionState, LiveConnectionEvent> = {
  idle: {
    wanted: connectingAfresh,
    unwanted: idle,
  },
  connecting: {
    opened: () => ({ status: "open" }),
    // A socket that never opened counts as one more failure.
    closed: (state, event) => afterClose(event.code, state.failures + 1),
    unwanted: idle,
    wanted: (state) => state,
  },
  open: {
    // A socket that was open drops: try again soon, the count starts over.
    closed: (_state, event) => afterClose(event.code, 0),
    unwanted: idle,
    wanted: (state) => state,
  },
  waitingToRetry: {
    retryDue: (state) => ({ status: "connecting", failures: state.failures }),
    retryRequested: connectingAfresh,
    unwanted: idle,
    wanted: (state) => state,
  },
  sessionEnded: {
    retryRequested: connectingAfresh,
    unwanted: idle,
    wanted: (state) => state,
  },
  refused: {
    retryRequested: connectingAfresh,
    unwanted: idle,
    wanted: (state) => state,
  },
};

export const transitionLiveConnection = defineStateMachine<LiveConnectionState, LiveConnectionEvent>(
  "live connection",
  LIVE_CONNECTION_TRANSITIONS,
);

/** Whether a socket should exist (opening or open) in this state. */
export function liveConnectionWantsSocket(state: LiveConnectionState): boolean {
  return state.status === "connecting" || state.status === "open";
}
